// Batch 0 (UX/IA overhaul) — one timestamp element, always tenant-tz-aware.
//
// Replaces the two timestamp bugs the audit found: CMS's
// `new Date().toLocaleString()` (browser-local, no tz label) and the
// Dashboard's `created_at.local` (reads `.local` off a plain string → blank).
// Routes everything through the tenant-tz formatter, which correctly handles
// BOTH a plain ISO string and the structured { utc, local, timezone } shape,
// and shows the full datetime + tz on hover so a relative time is never
// ambiguous for an auditor.

import {
  formatTimestamp,
  type TimestampInput,
} from "@/utils/datetime";
import { useTenantStore } from "@/store/tenant";
import { cn } from "@/lib/utils";

export interface TimestampProps {
  value: TimestampInput;
  /** "datetime" (default), "date", "time", or "relative". */
  format?: "datetime" | "date" | "time" | "relative";
  className?: string;
}

export function Timestamp({ value, format = "datetime", className }: TimestampProps) {
  const tz = useTenantStore((s) => s.timezone) || "Asia/Kolkata";
  const text = formatTimestamp(value, format, tz);
  // Absolute datetime + tz for the hover title — so a "5 min ago" is always
  // resolvable to an exact instant (audit requirement).
  const title =
    text === "—"
      ? undefined
      : `${formatTimestamp(value, "datetime", tz)} (${tz})`;
  return (
    <time className={cn("tabular-nums", className)} title={title}>
      {text}
    </time>
  );
}
